import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatRupeesVisual } from "@/lib/format";
import FYPicker from "@/components/FYPicker";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ReferenceLine,
} from "recharts";
import {
  Coffee, TrendingUp, RefreshCw, Sparkles, CalendarRange,
  CalendarDays, Calendar, ArrowUpRight, ArrowDownRight, Download,
} from "lucide-react";
import { toast } from "sonner";

const LINE_LABELS = {
  sales: "Sales",
  cogs: "COGS",
  rent: "Rent",
  salaries: "Salaries",
  utilities: "Utilities",
  royalty: "Royalty",
  other_expense: "Other",
};

const LINE_COLORS = {
  sales: "#059669",
  cogs: "#B45309",
  rent: "#334155",
  salaries: "#7C3AED",
  utilities: "#0891B2",
  royalty: "#DB2777",
  other_expense: "#64748B",
};

function PnlTooltip({ active, payload, label, stake }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-lg p-3 text-[12px] min-w-[220px]">
      <div className="font-semibold text-slate-900 mb-1.5">{label}</div>
      <div className="flex justify-between text-emerald-700">
        <span>Sales</span>
        <span className="font-mono-fin tabular-nums">{formatRupeesVisual(p.sales)}</span>
      </div>
      <div className="flex justify-between text-red-700">
        <span>Total expenses</span>
        <span className="font-mono-fin tabular-nums">−{formatRupeesVisual(p.total_expenses)}</span>
      </div>
      <div className="border-t border-slate-100 mt-1.5 pt-1.5 flex justify-between">
        <span className="text-slate-600">Net profit</span>
        <span className={`font-mono-fin font-semibold tabular-nums ${p.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
          {p.profit >= 0 ? "+" : ""}{formatRupeesVisual(p.profit)}
        </span>
      </div>
      <div className="flex justify-between text-slate-500 text-[11px]">
        <span>MARKSAM share ({stake}%)</span>
        <span className="font-mono-fin tabular-nums">{formatRupeesVisual(p.marksam_share)}</span>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone = "text-slate-900", icon: Icon, trend }) {
  const TrendIcn = trend === "up" ? ArrowUpRight : trend === "down" ? ArrowDownRight : null;
  const trendCls = trend === "up" ? "text-emerald-700 bg-emerald-50" : trend === "down" ? "text-red-600 bg-red-50" : "";
  return (
    <div className="p-4 border border-slate-200 bg-white rounded-md">
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-wider uppercase text-slate-500 flex items-center gap-1">
          {Icon && <Icon className="h-3 w-3" />} {label}
        </div>
        {TrendIcn && <span className={`p-1 rounded ${trendCls}`}><TrendIcn className="h-3 w-3" /></span>}
      </div>
      <div className={`mt-1.5 font-mono-fin text-xl sm:text-2xl font-semibold tabular-nums ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function CafePnL() {
  const [cafes, setCafes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [group, setGroup] = useState("month");
  const [fy, setFy] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    if (!selected) return;
    setPdfBusy(true);
    try {
      const qs = `group=${group}${fy === "all" ? "" : `&fy=${fy}`}`;
      const r = await api.get(`/cafe/${selected}/pnl/auto.pdf?${qs}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cafe-pnl-${(inv?.name || "cafe").replace(/\s+/g, "-")}-${group}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF generation failed");
    } finally { setPdfBusy(false); }
  };

  const load = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const qs = `group=${group}${fy === "all" ? "" : `&fy=${fy}`}`;
      const r = await api.get(`/cafe/${selected}/pnl/auto?${qs}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load P&L");
    } finally { setBusy(false); }
  };

  useEffect(() => {
    api.get("/investments").then((r) => {
      const c = (r.data || []).filter((i) => i.type === "business_franchise");
      setCafes(c);
      if (c.length > 0) setSelected(c[0].id);
    });
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected, group, fy]);

  const rows = useMemo(() => (data?.rows || []), [data]);
  const totals = data?.totals;
  const inv = data?.investment;
  const stake = data?.stake_percent || 12.5;
  const breakeven = data?.breakeven_target || 0;

  const trendOf = (key) => {
    if (rows.length < 2) return null;
    const a = rows[rows.length - 1][key], b = rows[rows.length - 2][key];
    if (a > b * 1.005) return "up";
    if (a < b * 0.995) return "down";
    return null;
  };

  const chartRows = useMemo(() => rows.slice(-12), [rows]);

  const delta = useMemo(() => {
    if (rows.length < 2) return null;
    const cur = rows[rows.length - 1];
    const prev = rows[rows.length - 2];
    const dPct = prev.sales !== 0 ? ((cur.sales - prev.sales) / Math.abs(prev.sales)) * 100 : 0;
    const pPct = prev.profit !== 0 ? ((cur.profit - prev.profit) / Math.abs(prev.profit)) * 100 : 0;
    return { cur, prev, dPct, pPct };
  }, [rows]);

  if (cafes.length === 0) {
    return (
      <div className="p-12 text-center text-slate-500">
        <Coffee className="h-10 w-10 mx-auto text-slate-300 mb-3" />
        No franchise investment found. Add one from Investments.
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="eyebrow flex items-center gap-1.5">
            <Coffee className="h-3.5 w-3.5 text-red-600" />
            FRANCHISE · OPERATIONS
          </div>
          <h1 className="mt-2 font-editorial font-light text-[36px] leading-[1.02] sm:text-5xl md:text-6xl text-slate-900 tracking-[-0.035em]" data-testid="cafe-title">
            {inv ? inv.name : "Cafe P&L"}<em className="italic">.</em>
          </h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            Live P&L pulled straight from Cash Book receipts, payments, and invoices tagged to this project. No manual entry — every rupee is sourced.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {cafes.length > 1 && (
            <Select value={selected || ""} onValueChange={setSelected}>
              <SelectTrigger className="w-48 h-9 text-[12px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {cafes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <FYPicker value={fy} onChange={setFy} size="sm" allowAll />
          <Button onClick={load} variant="outline" className="h-9 w-9 p-0 rounded-md" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
          </Button>
          <Button onClick={downloadPdf} disabled={pdfBusy || !data || rows.length === 0}
            className="h-9 gap-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="cafe-download-pdf" title="Branded one-pager — share with investors"
            aria-label={pdfBusy ? "Preparing PDF" : "Download branded P&L PDF"}>
            <Download className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">{pdfBusy ? "Preparing…" : "Download PDF"}</span>
            <span className="sm:hidden" aria-hidden>{pdfBusy ? "…" : "PDF"}</span>
          </Button>
        </div>
      </div>

      {/* View toggle */}
      <div className="inline-flex border border-slate-200 rounded-md p-0.5 bg-white" data-testid="cafe-view-toggle">
        {[
          { v: "week", l: "Weekly", ic: CalendarDays },
          { v: "month", l: "Monthly", ic: Calendar },
          { v: "year", l: "Yearly", ic: CalendarRange },
        ].map((opt) => (
          <button key={opt.v} onClick={() => setGroup(opt.v)}
            data-testid={`cafe-view-${opt.v}`}
            className={`inline-flex items-center gap-1.5 text-[12px] px-3 sm:px-4 py-1.5 rounded transition-colors ${
              group === opt.v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}>
            <opt.ic className="h-3.5 w-3.5" /> {opt.l}
          </button>
        ))}
      </div>

      {/* Hero KPI row */}
      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Total sales" value={formatRupeesVisual(totals.sales)}
            sub={`${rows.length} period${rows.length === 1 ? "" : "s"}${delta ? ` · ${delta.dPct >= 0 ? "+" : ""}${delta.dPct.toFixed(1)}% vs prev` : ""}`}
            icon={TrendingUp} trend={trendOf("sales")} />
          <Kpi label="Total expenses" value={formatRupeesVisual(totals.total_expenses)}
            sub={`COGS ${formatRupeesVisual(totals.cogs)} · Rent ${formatRupeesVisual(totals.rent)}`}
            trend={trendOf("total_expenses") === "up" ? "down" : trendOf("total_expenses") === "down" ? "up" : null} />
          <Kpi label="Net profit"
            value={`${totals.profit >= 0 ? "+" : ""}${formatRupeesVisual(totals.profit)}`}
            sub={`${totals.sales > 0 ? `${((totals.profit / totals.sales) * 100).toFixed(1)}% margin` : "no sales yet"}`}
            tone={totals.profit >= 0 ? "text-emerald-700" : "text-red-600"}
            trend={trendOf("profit")} />
          <Kpi label={`MARKSAM share · ${stake}%`}
            value={formatRupeesVisual(totals.marksam_share)}
            sub="Your slice of the profit"
            tone={totals.marksam_share >= 0 ? "text-emerald-700" : "text-red-600"}
            icon={Sparkles} />
        </div>
      )}

      {/* Chart — Sales vs Expenses + profit line + breakeven */}
      <div className="border border-slate-200 bg-white rounded-md p-4 sm:p-5">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="text-[13px] font-semibold text-slate-900">Sales vs Expenses · Net profit trend</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {group === "month" ? "Last 12 months" : group === "week" ? "Last 12 weeks" : "Per calendar year"}
              {breakeven > 0 && <> · <span className="text-red-600 font-medium">breakeven at {formatRupeesVisual(breakeven)}</span></>}
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-emerald-500 inline-block" /> Sales</span>
            <span className="inline-flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-slate-400 inline-block" /> Expenses</span>
            <span className="inline-flex items-center gap-1"><span className="h-0.5 w-3 bg-red-600 inline-block" /> Profit</span>
          </div>
        </div>
        <div className="h-[240px] sm:h-[300px]" data-testid="cafe-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartRows} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false}
                tickFormatter={(v) => formatRupeesVisual(v)} />
              <Tooltip content={<PnlTooltip stake={stake} />} />
              {breakeven > 0 && (
                <ReferenceLine y={breakeven} stroke="#DC2626" strokeDasharray="4 4"
                  label={{ value: "Breakeven", fill: "#DC2626", fontSize: 10, position: "insideTopRight" }} />
              )}
              <Bar dataKey="sales" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Bar dataKey="total_expenses" fill="#94A3B8" radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Line type="monotone" dataKey="profit" stroke="#DC2626" strokeWidth={2} dot={{ r: 3, fill: "#DC2626" }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Latest period delta + expense breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Expense mix */}
        {totals && totals.total_expenses > 0 && (
          <div className="border border-slate-200 bg-white rounded-md p-4 sm:p-5">
            <div className="text-[13px] font-semibold text-slate-900 mb-3">Where the money went</div>
            <ul className="space-y-2" data-testid="cafe-expense-mix">
              {["cogs", "rent", "salaries", "utilities", "royalty", "other_expense"].map((k) => {
                const v = totals[k] || 0;
                const pct = totals.total_expenses > 0 ? (v / totals.total_expenses) * 100 : 0;
                return (
                  <li key={k}>
                    <div className="flex items-center justify-between text-[12px] mb-1">
                      <span className="inline-flex items-center gap-1.5 text-slate-700">
                        <span className="h-2 w-2 rounded-full inline-block" style={{ background: LINE_COLORS[k] }} />
                        {LINE_LABELS[k]}
                      </span>
                      <span className="font-mono-fin text-slate-900 tabular-nums">{formatRupeesVisual(v)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: LINE_COLORS[k] }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Latest period card */}
        {delta && (
          <div className="border border-slate-200 bg-white rounded-md p-4 sm:p-5 lg:col-span-2">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[13px] font-semibold text-slate-900">Latest period · {delta.cur.label}</div>
              <span className={`text-[11px] font-mono-fin px-1.5 py-0.5 rounded ${delta.pPct >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                {delta.pPct >= 0 ? "+" : ""}{delta.pPct.toFixed(1)}% profit vs prev
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Sales</div>
                <div className="mt-1 font-mono-fin text-lg font-semibold text-slate-900 tabular-nums">{formatRupeesVisual(delta.cur.sales)}</div>
                <div className={`text-[11px] font-mono-fin tabular-nums ${delta.cur.sales >= delta.prev.sales ? "text-emerald-700" : "text-red-600"}`}>
                  {delta.cur.sales >= delta.prev.sales ? "+" : ""}{formatRupeesVisual(delta.cur.sales - delta.prev.sales)} vs prev
                </div>
              </div>
              <div className="p-3 rounded-md bg-white border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Net profit</div>
                <div className={`mt-1 font-mono-fin text-lg font-semibold tabular-nums ${delta.cur.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {delta.cur.profit >= 0 ? "+" : ""}{formatRupeesVisual(delta.cur.profit)}
                </div>
                <div className={`text-[11px] font-mono-fin tabular-nums ${delta.cur.profit >= delta.prev.profit ? "text-emerald-700" : "text-red-600"}`}>
                  {delta.cur.profit >= delta.prev.profit ? "+" : ""}{formatRupeesVisual(delta.cur.profit - delta.prev.profit)} vs prev
                </div>
              </div>
              <div className="p-3 rounded-md bg-white border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Margin</div>
                <div className="mt-1 font-mono-fin text-lg font-semibold text-slate-900 tabular-nums">
                  {delta.cur.sales > 0 ? `${((delta.cur.profit / delta.cur.sales) * 100).toFixed(1)}%` : "—"}
                </div>
                <div className="text-[11px] text-slate-500">
                  {delta.prev.sales > 0 ? `prev ${((delta.prev.profit / delta.prev.sales) * 100).toFixed(1)}%` : "—"}
                </div>
              </div>
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700">MARKSAM share</div>
                <div className="mt-1 font-mono-fin text-lg font-semibold text-emerald-800 tabular-nums">
                  {formatRupeesVisual(delta.cur.marksam_share)}
                </div>
                <div className="text-[11px] text-emerald-700">
                  of {formatRupeesVisual(delta.cur.profit)} profit
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Data table */}
      <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Period</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Sales</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">COGS</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Rent</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Salaries</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Utilities</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Royalty</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Other</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Total exp</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Profit</th>
                <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={11} className="p-8 text-center text-slate-500">
                  No transactions tagged to this cafe yet. Record Cash Book receipts/payments with this project selected.
                </td></tr>
              )}
              {rows.slice().reverse().map((r) => (
                <tr key={r.period_key} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`pnl-row-${r.period_key}`}>
                  <td className="p-3 font-medium text-slate-900">{r.label}</td>
                  <td className="p-3 text-right font-mono-fin text-emerald-700 tabular-nums">{formatRupeesVisual(r.sales)}</td>
                  <td className="p-3 text-right font-mono-fin tabular-nums">{formatRupeesVisual(r.cogs)}</td>
                  <td className="p-3 text-right font-mono-fin tabular-nums">{formatRupeesVisual(r.rent)}</td>
                  <td className="p-3 text-right font-mono-fin tabular-nums">{formatRupeesVisual(r.salaries)}</td>
                  <td className="p-3 text-right font-mono-fin tabular-nums">{formatRupeesVisual(r.utilities)}</td>
                  <td className="p-3 text-right font-mono-fin tabular-nums">{formatRupeesVisual(r.royalty)}</td>
                  <td className="p-3 text-right font-mono-fin tabular-nums">{formatRupeesVisual(r.other_expense)}</td>
                  <td className="p-3 text-right font-mono-fin tabular-nums text-slate-700">{formatRupeesVisual(r.total_expenses)}</td>
                  <td className={`p-3 text-right font-mono-fin font-semibold tabular-nums ${r.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {r.profit >= 0 ? "+" : ""}{formatRupeesVisual(r.profit)}
                  </td>
                  <td className="p-3 text-right font-mono-fin tabular-nums text-red-700">{formatRupeesVisual(r.marksam_share)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <ul className="md:hidden divide-y divide-slate-100">
          {rows.length === 0 && (
            <li className="p-8 text-center text-slate-500 text-[13px]">
              No transactions tagged to this cafe yet. Record Cash Book entries with this project selected.
            </li>
          )}
          {rows.slice().reverse().map((r) => (
            <li key={r.period_key} className="p-3.5" data-testid={`pnl-mrow-${r.period_key}`}>
              <div className="flex items-baseline justify-between">
                <div className="text-[13px] font-semibold text-slate-900">{r.label}</div>
                <div className={`font-mono-fin text-[15px] font-semibold tabular-nums ${r.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                  {r.profit >= 0 ? "+" : ""}{formatRupeesVisual(r.profit)}
                </div>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
                <div className="flex justify-between text-slate-600">
                  <span>Sales</span><span className="font-mono-fin text-emerald-700 tabular-nums">{formatRupeesVisual(r.sales)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Expenses</span><span className="font-mono-fin tabular-nums">{formatRupeesVisual(r.total_expenses)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>COGS</span><span className="font-mono-fin tabular-nums">{formatRupeesVisual(r.cogs)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Rent</span><span className="font-mono-fin tabular-nums">{formatRupeesVisual(r.rent)}</span>
                </div>
              </div>
              <div className="mt-1.5 flex items-center justify-between text-[10.5px] text-slate-500">
                <span>Margin {r.sales > 0 ? `${((r.profit / r.sales) * 100).toFixed(1)}%` : "—"}</span>
                <span className="text-red-700 font-medium">MARKSAM {formatRupeesVisual(r.marksam_share)}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Source footnote */}
      {data && (
        <p className="text-[11px] text-slate-500">
          Sources: <span className="font-mono-fin">{data.sources.cash_txns}</span> cash/bank entries ·{" "}
          <span className="font-mono-fin">{data.sources.bills}</span> purchase bills ·{" "}
          <span className="font-mono-fin">{data.sources.invoices}</span> sales invoices.  Capital deployments and MARKSAM&apos;s profit-share transfers are excluded so the operating P&L reflects reality.
        </p>
      )}
    </div>
  );
}
